import type { AppointmentStatus } from '../api';
import type { ResolvedTheme } from '../context/ThemeContext';

/**
 * One source of truth for appointment-status color.
 *
 * These used to live as five separate maps (AdminCalendar, TodayAgenda,
 * AdminDashboard, MyBookings, AdminAnalytics) that disagreed with each other:
 * "confirmed" was brand blue in three places, `blue-500` in another, and
 * indigo `#6366f1` in the chart; "completed" was green in three and emerald in
 * a fourth. Everything now resolves to the same semantic tokens.
 *
 * The maps are keyed by `AppointmentStatus` as the API declares it, so adding a
 * status there fails the build here until every map covers it.
 */

/** Small colored dot, e.g. the agenda list. */
export const STATUS_DOT: Record<AppointmentStatus, string> = {
  pending: 'bg-warn',
  confirmed: 'bg-brand-600',
  completed: 'bg-success',
  cancelled: 'bg-muted-strong',
};

/** Status word rendered on a plain surface. */
export const STATUS_TEXT: Record<AppointmentStatus, string> = {
  pending: 'text-warn',
  confirmed: 'text-accent',
  completed: 'text-success',
  cancelled: 'text-danger',
};

/** Pill / badge: wash plus matching foreground. */
export const STATUS_BADGE: Record<AppointmentStatus, string> = {
  pending: 'bg-warn-soft text-warn',
  confirmed: 'bg-accent-soft text-accent',
  completed: 'bg-success-soft text-success',
  cancelled: 'bg-muted text-fg-muted',
};

/**
 * Chart colors. Recharts takes hex props rather than classes, and the analytics
 * legend builds a translucent wash by concatenating an alpha suffix
 * (`${hex}20`), so these have to stay literal hex — a CSS variable would break
 * that concat. Keyed by resolved theme instead.
 */
export const STATUS_CHART: Record<ResolvedTheme, Record<AppointmentStatus, string>> = {
  light: {
    pending: '#b45309',
    confirmed: '#3a5fd9',
    completed: '#15803d',
    cancelled: '#9ca3af',
  },
  dark: {
    pending: '#f0b34b',
    confirmed: '#7f9df3',
    completed: '#5fd18b',
    cancelled: '#7d8595',
  },
};

/** Chart chrome that isn't tied to a status. */
export const CHART_PALETTE: Record<
  ResolvedTheme,
  { grid: string; axis: string; series: string; tooltipBg: string; tooltipBorder: string }
> = {
  light: {
    grid: '#e5e7eb',
    axis: '#6b7280',
    series: '#3a5fd9',
    tooltipBg: '#ffffff',
    tooltipBorder: '#e5e7eb',
  },
  dark: {
    grid: '#2a3040',
    axis: '#9aa2b1',
    series: '#7f9df3',
    tooltipBg: '#1c2029',
    tooltipBorder: '#2a3040',
  },
};
